import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { getMetricsBatch } from '@/features/metrics/data';
import { queryKeys } from '@/shared/hooks';
import type { TestListItemDto } from '@flowqa/shared';

type TableMetricSeries = {
  dates: Date[];
  passRate: Array<number | null>;
  frequencyPerDay: Array<number | null>;
  flakeScore: Array<number | null>;
  averageDurationMs: Array<number | null>;
};

type UseTestsTableMetricsModelParams = {
  enableMetricsBatch: boolean;
  isAuthenticated: boolean;
  metricsEndDay?: string;
  metricsStartDay?: string;
  selectedOrganisationId: string | null;
  sparklineRangeDays: number;
  visibleRows: TestListItemDto[];
};

type UseTestsTableMetricsModelResult = {
  metricSeriesByTestId: Map<string, TableMetricSeries>;
};

export const useTestsTableMetricsModel = ({
  enableMetricsBatch,
  isAuthenticated,
  metricsEndDay,
  metricsStartDay,
  selectedOrganisationId,
  sparklineRangeDays,
  visibleRows,
}: UseTestsTableMetricsModelParams): UseTestsTableMetricsModelResult => {
  const metricsRange = sparklineRangeDays === 14 ? '14d' : sparklineRangeDays === 30 ? '30d' : '7d';
  const metricsRangeKey =
    metricsStartDay && metricsEndDay ? `${metricsStartDay}_${metricsEndDay}` : 'all';
  const metricsEntityKey = useMemo(
    () => visibleRows.map((row) => `${row.projectId}:${row.id}`).join(','),
    [visibleRows],
  );
  const metricsBatchQuery = useQuery({
    queryKey: queryKeys.metricsBatch(
      selectedOrganisationId ?? null,
      'test',
      metricsRange,
      metricsEntityKey,
      metricsRangeKey,
    ),
    enabled: enableMetricsBatch && isAuthenticated && visibleRows.length > 0,
    staleTime: 30_000,
    queryFn: async () =>
      await getMetricsBatch(
        {
          range: metricsRange,
          scope: 'test',
          startDay: metricsStartDay ?? null,
          endDay: metricsEndDay ?? null,
          entities: visibleRows.map((row) => ({ entityId: row.id, projectId: row.projectId })),
        },
        selectedOrganisationId ?? undefined,
      ),
  });

  const metricSeriesByTestId = useMemo(() => {
    const metricsByTestId = new Map(
      (metricsBatchQuery.data ?? []).map((item) => [item.entityId, item.metrics]),
    );
    const result = new Map<string, TableMetricSeries>();
    visibleRows.forEach((row) => {
      const queryData = metricsByTestId.get(row.id);
      if (!queryData) return;
      const { executionDurationAverage, executionTrendMetrics, filteredPassRate, flakeScoreTrend } =
        queryData.series;
      if (
        !Array.isArray(filteredPassRate) ||
        !Array.isArray(executionTrendMetrics) ||
        !Array.isArray(flakeScoreTrend) ||
        !Array.isArray(executionDurationAverage)
      ) {
        return;
      }
      const dates = queryData.metricsRangeDates.map((day) => dayjs(day).toDate());
      const passRate = filteredPassRate.map((point) => point.value);
      const frequencyPerDay = executionTrendMetrics.map((point) => point.value);
      const flakeScore = flakeScoreTrend.map((point) => point.value);
      const averageDurationMs = executionDurationAverage.map((point) =>
        point.value === null ? null : point.value * 1000,
      );
      if (
        dates.length === 0 ||
        passRate.length !== dates.length ||
        frequencyPerDay.length !== dates.length ||
        flakeScore.length !== dates.length ||
        averageDurationMs.length !== dates.length
      ) {
        return;
      }
      result.set(row.id, { dates, passRate, frequencyPerDay, flakeScore, averageDurationMs });
    });
    return result;
  }, [metricsBatchQuery.data, visibleRows]);

  return { metricSeriesByTestId };
};
